import type { DashboardCode, MeResponse } from '@taqon/contracts';
import { hasCapability } from '../../shared/session/session';

/** The Release 1 dashboards (FRD-RPT-002) and the permission that opens each. */
export const DASHBOARD_LINKS: readonly {
  readonly code: DashboardCode;
  readonly title: string;
  readonly capability: string;
}[] = [
  { code: 'ceo', title: 'CEO dashboard', capability: 'reporting.ceo_dashboard:view' },
  { code: 'coo', title: 'COO dashboard', capability: 'reporting.coo_dashboard:view' },
  { code: 'finance', title: 'Finance dashboard', capability: 'reporting.finance_dashboard:view' },
  { code: 'hr', title: 'HR dashboard', capability: 'reporting.hr_dashboard:view' },
  {
    code: 'procurement',
    title: 'Procurement dashboard',
    capability: 'reporting.procurement_dashboard:view',
  },
  {
    code: 'marketing',
    title: 'Marketing dashboard',
    capability: 'reporting.marketing_dashboard:view',
  },
];

/** The dashboards the session may open (UX only; the API enforces, SDD §22.4). */
export function openableDashboards(session: MeResponse | null | undefined) {
  return DASHBOARD_LINKS.filter((d) => hasCapability(session, d.capability));
}

export type PeriodPreset = 'month' | 'quarter' | 'year' | 'custom';

/** Today's date in the business time zone, `YYYY-MM-DD`. */
export function businessToday(timeZone: string, now: Date = new Date()): string {
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).formatToParts(now);
  const part = (type: string) => parts.find((p) => p.type === type)?.value ?? '';
  return `${part('year')}-${part('month')}-${part('day')}`;
}

/**
 * A preset period, from its first day to today (period to date): this month, this calendar
 * quarter or this calendar year.
 */
export function presetPeriod(
  preset: Exclude<PeriodPreset, 'custom'>,
  today: string,
): { from: string; to: string } {
  const [year = '', month = ''] = today.split('-');
  if (preset === 'year') return { from: `${year}-01-01`, to: today };
  if (preset === 'month') return { from: `${year}-${month}-01`, to: today };
  const quarterStart = Math.floor((Number(month) - 1) / 3) * 3 + 1;
  return { from: `${year}-${String(quarterStart).padStart(2, '0')}-01`, to: today };
}
